import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { api } from '@/lib/api'
import { WidgetPreview } from '@/components/WidgetPreview'

// @ts-ignore
const WIDGET_VERSION = typeof __WIDGET_VERSION__ !== 'undefined' ? __WIDGET_VERSION__ : 'latest'

interface LeadFieldTemplate {
  template_key: string
  template_name: string
  template_description: string
  business_type: string
  fields: { key: string; label: string; required: boolean; type: string }[]
}

interface OnboardingData {
  companyName: string
  botName: string
  greeting: string
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  theme: 'light' | 'dark' | 'auto'
  primaryColor: string
  quickActions: { label: string; actionType: string; actionValue: string }[]
  companyDescription: string
  servicesDescription: string
  tone: string
  language: string
}

const STEPS = [
  'welcome',
  'widgetAppearance',
  'aiPersonality',
  'quickActions',
  'leadForm',
  'install',
] as const

export const Onboarding: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const isSpanish = i18n.language?.startsWith('es')
  const [data, setData] = useState<OnboardingData>(() => ({
    companyName: '',
    botName: 'AI Assistant',
    greeting: t('widget.defaultGreeting'),
    position: 'bottom-right',
    theme: 'dark',
    primaryColor: '#006cd4',
    quickActions: [
      {
        label: isSpanish ? 'Ver precios' : 'See pricing',
        actionType: 'message',
        actionValue: isSpanish ? '¿Cuáles son vuestros planes y precios?' : 'What are your pricing plans?'
      },
      {
        label: isSpanish ? 'Solicitar demo' : 'Book a demo',
        actionType: 'message',
        actionValue: isSpanish ? 'Me gustaría reservar una demostración.' : 'I would like to book a demo.'
      },
    ],
    companyDescription: '',
    servicesDescription: '',
    tone: 'friendly',
    language: isSpanish ? 'es' : 'en',
  }))

  const [installCode, setInstallCode] = useState('')
  const [copied, setCopied] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [templates, setTemplates] = useState<LeadFieldTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState('')

  useEffect(() => {
    api.get('/lead-fields/templates')
      .then((res) => {
        if (res.data.success) setTemplates(res.data.data || [])
      })
      .catch(() => { /* templates are optional in onboarding */ })
  }, [])

  const getTemplateDisplayName = (template: LeadFieldTemplate) => {
    const translated = t(`leadFields.template_${template.template_key}`)
    return translated !== `leadFields.template_${template.template_key}` ? translated : template.template_name
  }

  const getTemplateDescription = (template: LeadFieldTemplate) => {
    const translated = t(`leadFields.template_${template.template_key}_desc`)
    return translated !== `leadFields.template_${template.template_key}_desc` ? translated : template.template_description
  }

  const updateField = <K extends keyof OnboardingData>(field: K, value: OnboardingData[K]) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  const addQuickAction = () => {
    setData(prev => ({
      ...prev,
      quickActions: [...prev.quickActions, { label: '', actionType: 'message', actionValue: '' }],
    }))
  }

  const updateQuickAction = (index: number, field: string, value: string) => {
    setData(prev => ({
      ...prev,
      quickActions: prev.quickActions.map((qa, i) => i === index ? { ...qa, [field]: value } : qa),
    }))
  }

  const removeQuickAction = (index: number) => {
    setData(prev => ({
      ...prev,
      quickActions: prev.quickActions.filter((_, i) => i !== index),
    }))
  }

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const saveConfig = async () => {
    setLoading(true)
    setSaveError('')
    try {
      // Save company (tenant) name
      if (data.companyName.trim()) {
        await api.put('/auth/me', { tenantName: data.companyName.trim() })
      }

      // Save widget appearance (including language so the dashboard reads the
      // same value from widget_config, not only from ai_config)
      await api.put('/widgets/config', {
        position: data.position,
        theme: data.theme,
        primaryColor: data.primaryColor,
        botName: data.botName,
        greeting: data.greeting,
        language: data.language,
      })

      // Save AI config
      await api.put('/widgets/ai-config', {
        companyDescription: data.companyDescription,
        servicesDescription: data.servicesDescription,
        tone: data.tone,
        language: data.language,
      })

      // Apply lead capture template (drives what data the AI collects)
      if (selectedTemplate) {
        await api.post('/lead-fields/apply-template', { templateKey: selectedTemplate })
      }

      // Save quick actions — remove existing ones first so re-running the
      // onboarding does not create duplicates
      const existingRes = await api.get('/quick-actions')
      if (existingRes.data.success) {
        for (const existing of existingRes.data.data || []) {
          await api.delete(`/quick-actions/${existing.id}`)
        }
      }
      for (const qa of data.quickActions) {
        if (qa.label && qa.actionValue) {
          await api.post('/quick-actions', qa)
        }
      }

      // Generate install code (queue pattern compatible with widget loader)
      const tenantId = user?.tenantId || 'your-tenant-id'
      setInstallCode(`<script>
  (function(w,d,s,o,f,js,fjs){
    w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(Array.prototype.slice.call(arguments))};
    js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','r3Converse','https://cdn.r3converse.com/widget.js?v=` + __WIDGET_VERSION__ + `'));

  // Retry mechanism: widget may take time to load
  function tryInit(attempts) {
    if (typeof r3Converse === 'function') {
      r3Converse('init', {
        tenantId: '${tenantId}',
        position: '${data.position}',
        theme: '${data.theme}'
      });
    } else if (attempts > 0) {
      setTimeout(function() { tryInit(attempts - 1); }, 500);
    } else {
      console.error('r3Converse widget failed to load after 10 attempts');
    }
  }
  tryInit(10);
</script>`)

      nextStep()
    } catch (error: any) {
      console.error('Failed to save config:', error)
      setSaveError(error?.response?.data?.error || t('onboarding.saveError') || 'Failed to save your configuration. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(installCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const progress = ((currentStep + 1) / STEPS.length) * 100

  const renderStep = () => {
    switch (STEPS[currentStep]) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl mb-4 text-[#006cd4]"><i className="fa-solid fa-rocket" /></div>
            <h2 className="text-2xl font-bold">{t('onboarding.welcome.title')}</h2>
            <p className="text-gray-400 max-w-md mx-auto">{t('onboarding.welcome.description')}</p>
            <p className="text-sm text-[#006cd4] max-w-md mx-auto">{t('onboarding.welcome.editableLater')}</p>
            <div className="bg-[#1a1a25] rounded-lg p-4 text-left">
              <h3 className="font-semibold mb-2">{t('onboarding.welcome.whatYouNeed')}</h3>
              <ul className="text-sm text-gray-400 space-y-1">
                <li><i className="fa-solid fa-check text-green-400 mr-2" /> {t('onboarding.welcome.needs.companyName')}</li>
                <li><i className="fa-solid fa-check text-green-400 mr-2" /> {t('onboarding.welcome.needs.website')}</li>
                <li><i className="fa-solid fa-check text-green-400 mr-2" /> {t('onboarding.welcome.needs.services')}</li>
                <li><i className="fa-solid fa-check text-green-400 mr-2" /> {t('onboarding.welcome.needs.5minutes')}</li>
              </ul>
            </div>
            <button
              onClick={nextStep}
              className="px-8 py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors"
            >
              {t('onboarding.start')} <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        )

      case 'widgetAppearance':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold">{t('onboarding.appearance.title')}</h2>
              <p className="text-gray-400 text-sm mt-1">{t('onboarding.appearance.description')}</p>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-1 text-[#006cd4]">{t('onboarding.appearance.helpTitle')}</h3>
              <p className="text-sm text-gray-400">{t('onboarding.appearance.helpText')}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.botName')}</label>
                <input
                  type="text"
                  value={data.botName}
                  onChange={(e) => updateField('botName', e.target.value)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="AI Assistant"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.companyName')}</label>
                <input
                  type="text"
                  value={data.companyName}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                  placeholder="Your Company"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.greeting')}</label>
              <textarea
                value={data.greeting}
                onChange={(e) => updateField('greeting', e.target.value)}
                rows={3}
                className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">{t('onboarding.appearance.greetingHint')}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.position')}</label>
                <select
                  value={data.position}
                  onChange={(e) => updateField('position', e.target.value as any)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="bottom-right">{t('widget.positionOptions.bottomRight')}</option>
                  <option value="bottom-left">{t('widget.positionOptions.bottomLeft')}</option>
                  <option value="top-right">{t('widget.positionOptions.topRight')}</option>
                  <option value="top-left">{t('widget.positionOptions.topLeft')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.theme')}</label>
                <select
                  value={data.theme}
                  onChange={(e) => updateField('theme', e.target.value as any)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="dark">{t('widget.themeOptions.dark')}</option>
                  <option value="light">{t('widget.themeOptions.light')}</option>
                  <option value="auto">{t('widget.themeOptions.auto')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('onboarding.appearance.primaryColor')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={data.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={data.primaryColor}
                  onChange={(e) => updateField('primaryColor', e.target.value)}
                  className="flex-1 bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white font-mono text-sm"
                />
              </div>
            </div>

            <WidgetPreview
              config={{
                position: data.position,
                theme: data.theme,
                primaryColor: data.primaryColor,
                botName: data.botName,
                greeting: data.greeting,
                placeholder: t('widget.previewPlaceholder'),
                showPoweredBy: true,
                iconStyle: 'brand',
              }}
              title={t('onboarding.appearance.preview')}
            />
          </div>
        )

      case 'aiPersonality':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold">{t('onboarding.ai.title')}</h2>
              <p className="text-gray-400 text-sm mt-1">{t('onboarding.ai.description')}</p>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-1 text-[#006cd4]">{t('onboarding.ai.helpTitle')}</h3>
              <p className="text-sm text-gray-400">{t('onboarding.ai.helpText')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('onboarding.ai.companyDescription')}</label>
              <textarea
                value={data.companyDescription}
                onChange={(e) => updateField('companyDescription', e.target.value)}
                rows={4}
                className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                placeholder={t('onboarding.ai.companyDescriptionPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('onboarding.ai.companyDescriptionHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('onboarding.ai.servicesDescription')}</label>
              <textarea
                value={data.servicesDescription}
                onChange={(e) => updateField('servicesDescription', e.target.value)}
                rows={4}
                className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                placeholder={t('onboarding.ai.servicesDescriptionPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('onboarding.ai.servicesDescriptionHint')}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.ai.tone')}</label>
                <select
                  value={data.tone}
                  onChange={(e) => updateField('tone', e.target.value)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="professional">{t('widget.toneOptions.professional')}</option>
                  <option value="friendly">{t('widget.toneOptions.friendly')}</option>
                  <option value="casual">{t('widget.toneOptions.casual')}</option>
                  <option value="technical">{t('widget.toneOptions.technical')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">{t('onboarding.ai.language')}</label>
                <select
                  value={data.language}
                  onChange={(e) => updateField('language', e.target.value)}
                  className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-2">{t('onboarding.ai.preview')}</h3>
              <div className="text-sm text-gray-400 space-y-1">
                <p><i className="fa-solid fa-robot w-5" /> {t('onboarding.ai.previewBotName')}: <span className="text-white">{data.botName}</span></p>
                <p><i className="fa-solid fa-masks-theater w-5" /> {t('onboarding.ai.previewTone')}: <span className="text-white">{data.tone}</span></p>
                <p><i className="fa-solid fa-globe w-5" /> {t('onboarding.ai.previewLanguage')}: <span className="text-white">{data.language === 'en' ? 'English' : 'Español'}</span></p>
              </div>
            </div>
          </div>
        )

      case 'quickActions':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold">{t('onboarding.quickActions.title')}</h2>
              <p className="text-gray-400 text-sm mt-1">{t('onboarding.quickActions.description')}</p>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-1 text-[#006cd4]">{t('onboarding.quickActions.helpTitle')}</h3>
              <p className="text-sm text-gray-400">{t('onboarding.quickActions.helpText')}</p>
            </div>

            <div className="space-y-3">
              {data.quickActions.map((qa, index) => (
                <div key={index} className="bg-[#1a1a25] rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{t('onboarding.quickActions.action')} #{index + 1}</span>
                    <button
                      onClick={() => removeQuickAction(index)}
                      className="text-red-400 hover:text-red-300 text-sm"
                      aria-label={t('common.remove')}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={qa.label}
                      onChange={(e) => updateQuickAction(index, 'label', e.target.value)}
                      placeholder={t('onboarding.quickActions.label')}
                      className="bg-[#0a0a0f] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                    <select
                      value={qa.actionType}
                      onChange={(e) => updateQuickAction(index, 'actionType', e.target.value)}
                      className="bg-[#0a0a0f] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="message"><i className="fa-solid fa-comment" /> {t('onboarding.quickActions.type.message')}</option>
                      <option value="link"><i className="fa-solid fa-link" /> {t('onboarding.quickActions.type.link')}</option>
                      <option value="form"><i className="fa-solid fa-clipboard-list" /> {t('onboarding.quickActions.type.form')}</option>
                    </select>
                    <input
                      type="text"
                      value={qa.actionValue}
                      onChange={(e) => updateQuickAction(index, 'actionValue', e.target.value)}
                      placeholder={qa.actionType === 'link' ? 'https://...' : t('onboarding.quickActions.value')}
                      className="bg-[#0a0a0f] border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addQuickAction}
              className="w-full py-2 border border-dashed border-white/20 rounded-lg text-gray-400 hover:text-white hover:border-white/40 transition-colors"
            >
              <i className="fa-solid fa-plus mr-2" /> {t('onboarding.quickActions.add')}
            </button>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-3">{t('onboarding.quickActions.preview')}</h3>
              <div className="flex flex-wrap gap-2">
                {data.quickActions.map((qa, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 rounded-full text-xs bg-[#006cd4]/20 text-[#006cd4] border border-[#006cd4]/20"
                  >
                    {qa.label || `Action ${i + 1}`}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )

      case 'leadForm':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold">{t('onboarding.leadForm.title')}</h2>
              <p className="text-gray-400 text-sm mt-1">{t('onboarding.leadForm.description')}</p>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="text-sm font-medium mb-1 text-[#006cd4]">{t('onboarding.leadForm.howItWorksTitle')}</h3>
              <p className="text-sm text-gray-400">{t('onboarding.leadForm.howItWorksText')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">{t('onboarding.leadForm.selectTemplate')}</label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="w-full bg-[#1a1a25] border border-white/10 rounded-lg px-4 py-2 text-white"
              >
                <option value="">{t('onboarding.leadForm.noTemplate')}</option>
                {templates.map((template) => (
                  <option key={template.template_key} value={template.template_key}>
                    {getTemplateDisplayName(template)}
                  </option>
                ))}
              </select>
              {selectedTemplate && (
                <p className="text-xs text-gray-500 mt-1">
                  {getTemplateDescription(templates.find((tpl) => tpl.template_key === selectedTemplate)!)}
                </p>
              )}
            </div>

            {selectedTemplate && (
              <div className="bg-[#1a1a25] rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3">{t('onboarding.leadForm.fieldsPreview')}</h3>
                <div className="flex flex-wrap gap-2">
                  {templates
                    .find((tpl) => tpl.template_key === selectedTemplate)
                    ?.fields.map((field) => {
                      const i18nKey = `leadFields.fieldLabels.${field.key}`
                      const translatedLabel = t(i18nKey)
                      const label = translatedLabel !== i18nKey ? translatedLabel : field.label
                      return (
                        <span
                          key={field.key}
                          className="px-3 py-1.5 rounded-full text-xs bg-[#006cd4]/20 text-[#006cd4] border border-[#006cd4]/20"
                        >
                          {label}
                          {field.required && <span className="ml-1 text-white/60">*</span>}
                        </span>
                      )
                    })}
                </div>
                <p className="text-xs text-gray-500 mt-3">{t('onboarding.leadForm.fieldsHint')}</p>
              </div>
            )}

            <div className="bg-[#1a1a25] rounded-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-lg"><i className="fa-solid fa-check" /></div>
                <div>
                  <p className="font-medium">{t('onboarding.leadForm.autoCapture')}</p>
                  <p className="text-sm text-gray-400">{t('onboarding.leadForm.autoCaptureDesc')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-lg"><i className="fa-solid fa-check" /></div>
                <div>
                  <p className="font-medium">{t('onboarding.leadForm.emailNotification')}</p>
                  <p className="text-sm text-gray-400">{t('onboarding.leadForm.emailNotificationDesc')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-sm text-gray-400">
                <i className="fa-solid fa-pen-to-square mr-2 text-[#006cd4]" /> {t('onboarding.leadForm.editLaterText')}
              </p>
            </div>

            <div className="bg-[#006cd4]/10 border border-[#006cd4]/20 rounded-lg p-4">
              <p className="text-sm text-[#006cd4]">
                <i className="fa-solid fa-lightbulb mr-2" /> {t('onboarding.leadForm.tip')}
              </p>
            </div>
          </div>
        )

      case 'install':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4 text-[#00d4aa]"><i className="fa-solid fa-champagne-glasses" /></div>
              <h2 className="text-2xl font-bold">{t('onboarding.install.title')}</h2>
              <p className="text-gray-400 mt-2">{t('onboarding.install.description')}</p>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{t('onboarding.install.code')}</span>
                <button
                  onClick={copyToClipboard}
                  className="text-xs text-[#006cd4] hover:underline"
                >
                  {copied ? <><i className="fa-solid fa-check mr-1" /> {t('common.copied')}</> : t('common.copy')}
                </button>
              </div>
              <pre className="bg-[#0a0a0f] rounded-lg p-4 text-sm text-gray-300 overflow-x-auto font-mono">
                <code>{installCode}</code>
              </pre>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4">
              <h3 className="font-medium mb-3">{t('onboarding.install.platformsTitle')}</h3>
              <ul className="text-sm text-gray-400 space-y-2">
                <li><i className="fa-brands fa-wordpress w-5 text-center text-[#006cd4]" /> {t('onboarding.install.platformWordpress')}</li>
                <li><i className="fa-brands fa-shopify w-5 text-center text-[#00d4aa]" /> {t('onboarding.install.platformShopify')}</li>
                <li><i className="fa-solid fa-w w-5 text-center text-gray-500" /> {t('onboarding.install.platformWix')}</li>
                <li><i className="fa-solid fa-square w-5 text-center text-gray-500" /> {t('onboarding.install.platformSquarespace')}</li>
                <li><i className="fa-solid fa-wand-magic-sparkles w-5 text-center text-[#006cd4]" /> {t('onboarding.install.platformWebflow')}</li>
                <li><i className="fa-solid fa-globe w-5 text-center text-gray-500" /> {t('onboarding.install.platformOther')}</li>
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium">{t('onboarding.install.steps')}</h3>
              <ol className="text-sm text-gray-400 space-y-2 list-decimal list-inside">
                <li>{t('onboarding.install.step1')}</li>
                <li>{t('onboarding.install.step2')}</li>
                <li>{t('onboarding.install.step3')}</li>
                <li>{t('onboarding.install.step4')}</li>
              </ol>
            </div>

            <div className="bg-[#1a1a25] rounded-lg p-4 space-y-2">
              <p className="text-sm text-gray-400">
                <i className="fa-solid fa-code mr-2 text-[#006cd4]" /> {t('onboarding.install.noCodeNote')}
              </p>
              <p className="text-sm text-gray-400">
                <i className="fa-solid fa-lock mr-2" /> {t('onboarding.install.privacyNote')}
              </p>
              <p className="text-sm text-gray-400">
                <i className="fa-solid fa-book-open mr-2" /> {t('onboarding.install.helpNote')}{' '}
                <button
                  onClick={() => navigate('/help')}
                  className="text-[#006cd4] hover:underline"
                >
                  {t('onboarding.install.helpLink')}
                </button>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/widget')}
                className="flex-1 py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors"
              >
                {t('onboarding.install.goToDashboard')}
              </button>
              <button
                onClick={() => navigate('/conversations')}
                className="flex-1 py-3 border border-white/10 text-white font-semibold rounded-lg hover:bg-white/5 transition-colors"
              >
                {t('onboarding.install.viewConversations')}
              </button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">
              {t('onboarding.step')} {currentStep + 1} {t('onboarding.of')} {STEPS.length}
            </span>
            <span className="text-sm text-gray-400">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-[#1a1a25] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#006cd4] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2">
            {STEPS.map((step, index) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full ${
                  index <= currentStep ? 'bg-[#006cd4]' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-[#12121a] rounded-2xl border border-white/10 p-8">
          {renderStep()}
        </div>

        {/* Save error */}
        {saveError && (
          <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {saveError}
          </div>
        )}

        {/* Navigation */}
        {currentStep > 0 && currentStep < STEPS.length - 1 && (
          <div className="flex justify-between mt-6">
            <button
              onClick={prevStep}
              className="px-6 py-2 border border-white/10 rounded-lg text-white hover:bg-white/5 transition-colors"
            >
              <i className="fa-solid fa-arrow-left mr-2" /> {t('common.back')}
            </button>
            {currentStep === STEPS.length - 2 ? (
              <button
                onClick={saveConfig}
                disabled={loading}
                className="px-6 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <i className="fa-solid fa-spinner fa-spin" />
                    {t('common.saving')}
                  </span>
                ) : (
                  <>
                    {t('onboarding.finish')} <i className="fa-solid fa-arrow-right ml-2" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="px-6 py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors"
              >
                {t('common.next')} <i className="fa-solid fa-arrow-right ml-2" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
